import { Host, Component, Prop, h, Event, EventEmitter } from '@stencil/core';

@Component({
  tag: 'dda-link-button',
  styleUrls: [],
  shadow: false,
})
export class DdaLinkButton {
  @Prop() disabled: boolean = false;
  @Prop() start_icon: string = '';
  @Prop() link_button_text: string = '';
  @Prop() link_button_title: string = '';
  @Prop() end_icon: string = '';
  @Prop() aria_label?: string = '';
  @Prop() button_color: string = 'primary';
  @Prop() size?: string;
  @Prop() button_shape?: string = '';
  @Prop() icon_button_shape?: string = '';
  @Prop() gap?: number;
  @Prop() custom_class?: string = '';
  @Prop() href?: string = '#';
  @Prop() component_mode?: string; 
  @Prop() button_id: string;
  @Prop() target?: string = '';
  @Prop() rel?: string = 'noopener noreferrer';
  @Prop() data_fancybox?: string;
  @Prop() clickHandler?: (event: MouseEvent) => void;

  // Add event emitter for better integration
  @Event() ddaLinkClick: EventEmitter<MouseEvent>;

  private handleClick = (event: MouseEvent) => {
    // Block all click events when disabled
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }

    // Emit custom event for parent components
    this.ddaLinkClick.emit(event);

    // Call custom click handler if provided
    if (this.clickHandler) {
      this.clickHandler(event);
    }
  };

  render() {
    const linkbuttonClass = [
      'dda-btn',
      `btn-color-${this.button_color}`,
      this.size ? `btn-size-${this.size}` : '',
      this.button_shape ? `btn-shape-${this.button_shape}` : '',
      this.icon_button_shape ? `icon-btn-${this.icon_button_shape}` : '',
      this.gap ? `dda-gap-${this.gap}` : '',
      this.disabled ? 'disabled' : '', // Add disabled class
      this.custom_class,
      this.component_mode,
    ].filter(Boolean).join(' ');

    // Generate aria-label if not provided
    const ariaLabel = this.aria_label || this.link_button_text || 'Link button';

    // Handle target security
    const linkTarget = this.target;
    const linkRel = this.target === '_blank' ? 'noopener noreferrer' : this.rel;

    return (
      <Host>
        <a
          id={this.button_id}
          target={linkTarget}  
          rel={linkRel}
          data-fancybox={this.data_fancybox}
          title={this.link_button_title}
          href={this.disabled ? undefined : this.href} // Remove href when disabled
          class={linkbuttonClass}
          aria-label={ariaLabel}
          aria-disabled={this.disabled ? 'true' : undefined}
          tabindex={this.disabled ? -1 : undefined} // Remove from tab order when disabled
          onClick={this.handleClick}
          onKeyDown={(event) => {
            // Block keyboard navigation when disabled
            if (this.disabled && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              event.stopPropagation();
              event.stopImmediatePropagation();
              return false;
            }
          }}
          style={this.disabled ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}}
        >
          {this.start_icon && (
            <i 
              class="material-icons material-symbols-outlined" 
              aria-hidden="true"
            >
              {this.start_icon}
            </i>
          )}
          {this.link_button_text}
          {this.end_icon && (
            <i 
              class="material-icons material-symbols-outlined" 
              aria-hidden="true"
            >
              {this.end_icon}
            </i>
          )}
        </a>
      </Host>
    );
  }
}